import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import AppIcon from './AppIcon';
import { cn } from '../utils/cn';

const getContextTitle = (pathname) => {
    if (pathname.includes('asset-registration')) return 'Adding Assets in Panasonic System';
    if (pathname.includes('dashboard')) return 'Asset Dashboard Context';
    if (pathname.includes('asset-list')) return 'Asset List Context';
    if (pathname.includes('lifecycle')) return 'Lifecycle Planning Context';
    if (pathname.includes('software')) return 'Software Dashboard Context';
    if (pathname.includes('checkout')) return 'Checkout Management Context';
    if (pathname.includes('supplier')) return 'Supplier Management Context';
    if (pathname.includes('admin/user-registration')) return 'User Registration Admin';
    if (pathname.includes('admin/employee-management')) return 'Employee Management Admin';
    if (pathname.includes('admin/department-management')) return 'Department Admin';
    if (pathname.includes('admin/location-management')) return 'Location Admin';
    if (pathname.includes('profile-settings')) return 'Profile Settings';
    if (pathname.includes('preferences')) return 'User Preferences';
    return 'Panasonic System Context';
};

const formatMessageContent = (content) => {
    if (!content) return null;
    
    // Split by lines to handle list items and paragraphs
    const lines = content.split('\n');
    
    return lines.map((line, lineIdx) => {
        let trimmed = line.trim();
        if (!trimmed) return <div key={lineIdx} className="h-2" />;
        
        // Handle list item
        const isListItem = trimmed.startsWith('-') || trimmed.startsWith('*') || /^\d+\./.test(trimmed);
        
        // Process inline code/buttons (e.g., `+ Add Asset`) and bold text (`**Option 1**`)
        const parts = [];
        let currentText = trimmed;
        if (isListItem) {
            // Strip out list marker
            currentText = trimmed.replace(/^[-*\d.]+\s*/, '');
        }
        
        // Simple parser for `code` and **bold**
        let regex = /(\*\*.*?\*\*|`.*?`)/g;
        let match;
        let lastIndex = 0;
        
        while ((match = regex.exec(currentText)) !== null) {
            const index = match.index;
            const matchStr = match[0];
            
            // Add preceding text
            if (index > lastIndex) {
                parts.push(currentText.substring(lastIndex, index));
            }
            
            if (matchStr.startsWith('**') && matchStr.endsWith('**')) {
                const innerText = matchStr.slice(2, -2);
                parts.push(<strong key={index} className="font-semibold text-white">{innerText}</strong>);
            } else if (matchStr.startsWith('`') && matchStr.endsWith('`')) {
                const innerText = matchStr.slice(1, -1);
                parts.push(
                    <code key={index} className="px-1.5 py-0.5 bg-[#2d2f31] text-xs text-[#e3e3e3] border border-[#3c4043] rounded font-mono mx-0.5">
                        {innerText}
                    </code>
                );
            }
            
            lastIndex = regex.lastIndex;
        }
        
        if (lastIndex < currentText.length) {
            parts.push(currentText.substring(lastIndex));
        }

        if (isListItem) {
            const isOptionHeader = trimmed.startsWith('Option') || trimmed.toLowerCase().startsWith('option');
            return (
                <div key={lineIdx} className={`pl-5 relative text-sm text-[#e3e3e3] ${isOptionHeader ? 'mt-4 mb-1.5 font-medium' : 'my-1'}`}>
                    <span className="absolute left-1 top-2 w-1.5 h-1.5 rounded-full bg-blue-400"></span>
                    {parts.length > 0 ? parts : currentText}
                </div>
            );
        }

        return (
            <p key={lineIdx} className="text-sm text-[#c4c7c5] leading-relaxed my-2">
                {parts.length > 0 ? parts : currentText}
            </p>
        );
    });
};

const AIChatbot = () => {
    const location = useLocation();
    const contextTitle = getContextTitle(location.pathname);

    const [isOpen, setIsOpen] = useState(false);
    const [isSharingContext, setIsSharingContext] = useState(true);

    const [messages, setMessages] = useState([
        { role: 'ai', content: 'Hello! I am your Panasonic Asset Assistant. How can I help you today?' }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const scrollRef = useRef(null);

    // Auto-scroll to bottom of chat
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, isLoading]);


    const handleSendMessage = async (e) => {
        e?.preventDefault();
        if (!input.trim() || isLoading) return;

        const userQuestion = input.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', content: userQuestion }]);
        setIsLoading(true);

        try {
            // Append context information if sharing is enabled
            const fullPrompt = isSharingContext 
                ? `[Context: User is currently on screen "${contextTitle}"]. Question: ${userQuestion}`
                : userQuestion;

            const { data, error } = await supabase.functions.invoke('chat-with-assets', {
                body: { userQuestion: fullPrompt }
            });

            if (error) throw error;

            setMessages(prev => [...prev, { 
                role: 'ai', 
                content: data?.answer || "I'm sorry, I couldn't process that request." 
            }]);
        } catch (err) {
            console.error('Chat error:', err);
            setMessages(prev => [...prev, { 
                role: 'ai', 
                content: "I encountered an error connecting to my systems. Please try again later." 
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {/* Ask Gemini Style Right Side Panel */}
            <div 
                className={cn(
                    "bg-[#131314] flex flex-col h-full z-[100] transition-all duration-300 ease-in-out overflow-hidden border-l border-transparent",
                    // Mobile & Tablet: Fixed overlay
                    "fixed right-0 top-0 bottom-0 shadow-2xl sm:shadow-none",
                    isOpen ? "w-full translate-x-0" : "w-0 translate-x-full",
                    // Desktop (sm and up): Flex sibling, pushes content
                    "sm:relative sm:translate-x-0",
                    isOpen ? "sm:w-[380px] sm:border-[#2d2f31]" : "sm:w-0 sm:border-transparent"
                )}
            >
                {/* Header */}
                <div className="h-16 border-b border-[#2d2f31] px-4 flex items-center justify-between bg-[#1e1f20] text-white flex-shrink-0">
                    <span className="font-semibold text-sm truncate max-w-[200px]" title={contextTitle}>
                        {contextTitle}
                    </span>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button 
                            type="button"
                            onClick={() => setIsOpen(false)}
                            title="Close Assistant"
                            className="hover:bg-white/10 p-2 rounded-full transition-colors text-[#c4c7c5] hover:text-white"
                        >
                            <AppIcon name="X" size={16} />
                        </button>
                    </div>
                </div>

                {/* Message Log */}
                <div 
                    ref={scrollRef}
                    className="flex-1 overflow-y-auto p-4 space-y-6 bg-[#131314]"
                >
                    {messages.map((msg, idx) => {
                        if (msg.role === 'user') {
                            return (
                                <div key={idx} className="flex justify-end w-full">
                                    <div className="bg-[#2a2b2f] text-[#e3e3e3] px-4 py-2.5 rounded-[22px] max-w-[85%] text-sm leading-relaxed shadow-sm">
                                        {msg.content}
                                    </div>
                                </div>
                            );
                        } else {
                            return (
                                <div key={idx} className="flex flex-col items-start w-full">
                                    <div className="text-blue-400 mb-2 flex items-center justify-center bg-blue-500/10 p-1.5 rounded-lg">
                                        <AppIcon name="Sparkles" size={16} strokeWidth={2} />
                                    </div>
                                    <div className="w-full text-sm text-[#e3e3e3] space-y-1">
                                        {formatMessageContent(msg.content)}
                                    </div>
                                </div>
                            );
                        }
                    })}
                    {isLoading && (
                        <div className="flex flex-col items-start w-full animate-pulse">
                            <div className="text-blue-400 mb-2 flex items-center justify-center bg-blue-500/10 p-1.5 rounded-lg">
                                <AppIcon name="Sparkles" size={16} className="animate-spin duration-1000" />
                            </div>
                            <div className="w-full space-y-2">
                                <div className="h-4 bg-[#2d2f31] rounded w-3/4"></div>
                                <div className="h-4 bg-[#2d2f31] rounded w-5/6"></div>
                                <div className="h-4 bg-[#2d2f31] rounded w-1/2"></div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Bottom Context Sharing Bar */}
                {isSharingContext && (
                    <div className="mx-4 my-2 px-3 py-2 bg-[#1c2b3d]/60 border border-[#2d3b4d]/80 rounded-xl flex items-center justify-between flex-shrink-0 animate-in fade-in duration-200">
                        <div className="flex items-center gap-2 overflow-hidden">
                            <AppIcon name="FileText" size={13} className="text-blue-400 flex-shrink-0" />
                            <span className="text-[10.5px] text-[#c4c7c5] font-medium truncate">
                                Sharing "{contextTitle}" context
                            </span>
                        </div>
                        <button 
                            type="button"
                            onClick={() => setIsSharingContext(false)}
                            title="Stop sharing screen context"
                            className="text-[#9aa0a6] hover:text-[#e3e3e3] p-0.5 rounded hover:bg-white/5 transition-colors"
                        >
                            <AppIcon name="X" size={11} />
                        </button>
                    </div>
                )}

                {/* Input Controls */}
                <div className="p-4 bg-[#131314] border-t border-[#2d2f31] flex-shrink-0">
                    <form 
                        onSubmit={handleSendMessage}
                        className="bg-[#1e1f20] border border-[#2d2f31] rounded-full px-4 py-2 flex items-center gap-2 focus-within:border-blue-500/50 transition-colors"
                    >
                        <textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendMessage();
                                }
                            }}
                            placeholder="Ask me anything about assets..."
                            rows={1}
                            style={{ minHeight: '24px', maxHeight: '120px' }}
                            className="flex-1 bg-transparent border-none outline-none resize-none text-sm text-[#e3e3e3] placeholder-[#9aa0a6] py-0.5 focus:ring-0 leading-normal"
                            disabled={isLoading}
                        />
                        <button
                            type="submit"
                            disabled={!input.trim() || isLoading}
                            className="bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white p-2 rounded-full transition-colors active:scale-95 flex items-center justify-center flex-shrink-0"
                            title="Send message"
                        >
                            <AppIcon name="Send" size={14} color="white" />
                        </button>
                    </form>
                </div>
            </div>

            {/* Floating FAB Toggle Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                    "w-14 h-14 rounded-full shadow-2xl items-center justify-center transition-all duration-300 active:scale-95 group fixed z-[100]",
                    isOpen 
                        ? "hidden sm:flex sm:bottom-6 sm:right-[396px] bg-card text-foreground border border-border" 
                        : "flex bottom-4 right-4 sm:bottom-6 sm:right-6 bg-primary text-primary-foreground hover:scale-105"
                )}
            >
                {isOpen ? (
                    <AppIcon name="Minus" size={28} />
                ) : (
                    <div className="relative">
                        <AppIcon name="Sparkles" size={28} />
                        <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 border-2 border-primary rounded-full animate-pulse"></span>
                    </div>
                )}
            </button>
        </>
    );
};

export default AIChatbot;
